import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

const RegistrarJefes = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const isEdit = useMemo(() => Boolean(id), [id]);

  // Información básica del Jefe
  const [documentoJefe, setDocumentoJefe] = useState("");
  const [tipodocumentoJefe, setTipodocumentoJefe] = useState("");
  const [nombre1Jefe, setNombre1Jefe] = useState("");
  const [nombre2Jefe, setNombre2Jefe] = useState("");
  const [apellido1Jefe, setApellido1Jefe] = useState("");
  const [apellido2Jefe, setApellido2Jefe] = useState("");
  const [emailJefe, setEmailJefe] = useState("");

  // Información de contacto
  const [telefono1Jefe, setTelefono1Jefe] = useState("");
  const [telefono2Jefe, setTelefono2Jefe] = useState("");

  const cleanData = useCallback(() => {
    setDocumentoJefe("");
    setTipodocumentoJefe("");
    setNombre1Jefe("");
    setNombre2Jefe("");
    setApellido1Jefe("");
    setApellido2Jefe("");
    setEmailJefe("");
    setTelefono1Jefe("");
    setTelefono2Jefe("");
  }, []);

  const getData = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`/api/jefe/listid/${id}`);
      if (!data?.ok) return;

      const jefe = data?.message;

      setNombre1Jefe(jefe?.nombre1Jefe || "");
      setNombre2Jefe(jefe?.nombre2Jefe || "");
      setApellido1Jefe(jefe?.apellido1Jefe || "");
      setApellido2Jefe(jefe?.apellido2Jefe || "");
      setTipodocumentoJefe(jefe?.tipodocumentoJefe || "");
      setDocumentoJefe(jefe?.documentoJefe || "");
      setTelefono1Jefe(jefe?.telefono1Jefe || "");
      setTelefono2Jefe(jefe?.telefono2Jefe || "");
      setEmailJefe(jefe?.emailJefe || "");
    } catch (error) {
      console.error("Error al obtener el jefe:", error?.message);
      Swal.fire("Error", "No se pudo cargar el jefe", "error");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) getData();
  }, [id, getData]);

  const saveJefe = async () => {
    try {
      setLoading(true);

      const newJefe = {
        documentoJefe,
        tipodocumentoJefe,
        nombre1Jefe,
        nombre2Jefe,
        apellido1Jefe,
        apellido2Jefe,
        emailJefe,
        telefono1Jefe,
        telefono2Jefe,
      };

      const { data } = await axios.post("/api/jefe/add", newJefe);

      if (data?.ok === false) {
        return Swal.fire("Error", data?.message || "No se pudo registrar.", "error");
      }

      await Swal.fire({
        icon: "success",
        title: "El Jefe ha sido registrado con éxito",
        showConfirmButton: false,
        timer: 1500,
      });

      cleanData(); // ✅ al guardar
    } catch (error) {
      const msg = error.response?.data?.message || "Error al registrar el Jefe";
      Swal.fire("Error", msg, "error");
      console.error("Error en saveJefe:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateJefe = async () => {
    try {
      setLoading(true);

      const jefeData = {
        nombre1Jefe,
        nombre2Jefe,
        apellido1Jefe,
        apellido2Jefe,
        telefono1Jefe,
        telefono2Jefe,
        emailJefe,
      };

      const { data } = await axios.put(`/api/jefe/update/${id}`, jefeData);

      if (data?.ok === false) {
        return Swal.fire("Error", data?.message || "No se pudo actualizar.", "error");
      }

      await Swal.fire({
        icon: "success",
        title: data?.message || "El Jefe fue actualizado correctamente.",
        showConfirmButton: false,
        timer: 1500,
      });

      cleanData(); // ✅ al actualizar
      navigate(-1);
    } catch (error) {
      const msg = error.response?.data?.message || "Error al actualizar el Jefe";
      Swal.fire("Error", msg, "error");
      console.error("Error al actualizar el Jefe:", error);
    } finally {
      setLoading(false);
    }
  };

  const actions = async (e) => {
    e.preventDefault();
    isEdit ? await updateJefe() : await saveJefe();
  };

  return (
    <div className="bg-body-tertiary py-4">
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          {/* Header */}
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  {isEdit ? "Editar Jefe" : "Registrar Jefe"}
                </h5>
                <small className="text-secondary">
                  Completa la información del jefe inmediato.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  type="button"
                  className="btn btn-outline-dark"
                  onClick={() => navigate(-1)}
                  disabled={loading}
                  title="Volver">
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Volver
                </button>
                
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="p-3 p-md-4">
            {loading && (
              <div className="text-center text-secondary py-3">
                <div className="spinner-border" role="status" />
                <div className="mt-2">Procesando…</div>
              </div>
            )}

            <form id="form-jefe" onSubmit={actions} className="needs-validation" noValidate>
              <div className="row g-3">
                {/* Nombres */}
                <div className="col-12 col-md-3">
                  <label className="form-label fw-semibold">Nombre 1</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Ej: Juan"
                    value={nombre1Jefe}
                    onChange={(e) => setNombre1Jefe(e.target.value)}
                    required
                  />
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label fw-semibold">Nombre 2</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Opcional"
                    value={nombre2Jefe}
                    onChange={(e) => setNombre2Jefe(e.target.value)}
                  />
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label fw-semibold">Apellido 1</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Ej: Pérez"
                    value={apellido1Jefe}
                    onChange={(e) => setApellido1Jefe(e.target.value)}
                    required
                  />
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label fw-semibold">Apellido 2</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Opcional"
                    value={apellido2Jefe}
                    onChange={(e) => setApellido2Jefe(e.target.value)}
                  />
                </div>

                {/* Documento */}
                <div className="col-12 col-md-3">
                  <label className="form-label fw-semibold">Tipo de Documento</label>
                  <select
                    className="form-select"
                    value={tipodocumentoJefe}
                    onChange={(e) => setTipodocumentoJefe(e.target.value)}
                    required
                    disabled={isEdit}
                  >
                    <option value="">Seleccione…</option>
                    <option value="CC">Cédula de Ciudadanía</option>
                    <option value="CE">Cédula de Extranjería</option>
                    <option value="PAS">Pasaporte</option>
                  </select>
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label fw-semibold">Número de Documento</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Ej: 1234567890"
                    value={documentoJefe}
                    onChange={(e) => setDocumentoJefe(e.target.value)}
                    required
                    disabled={isEdit}
                  />
                </div>

                {/* Teléfonos */}
                <div className="col-12 col-md-3">
                  <label className="form-label fw-semibold">Teléfono 1</label>
                  <input
                    type="tel"
                    className="form-control"
                    placeholder="Ej: 3001234567"
                    value={telefono1Jefe}
                    onChange={(e) => setTelefono1Jefe(e.target.value)}
                    required
                  />
                </div>

                {/* Email */}
                <div className="col-12 col-md-3">
                  <label className="form-label fw-semibold">Correo Electrónico</label>
                  <input
                    type="email"
                    className="form-control"
                    placeholder="Ej: correo@dominio.com"
                    value={emailJefe}
                    onChange={(e) => setEmailJefe(e.target.value)}
                    required
                  />
                </div>

                {/* Footer botones */}
                <div className="col-12 pt-2">
                  <div className="d-flex flex-wrap gap-2 justify-content-end">
                    <button
                      type="button"
                      className="btn btn-outline-dark"
                      onClick={cleanData}
                      disabled={loading}
                      title="Limpiar"
                    >
                      <i className="fa-solid fa-eraser me-2"></i>
                      Limpiar
                    </button>

                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={() => {
                        cleanData();
                        navigate("/enteconformador/jefes/");
                      }}
                      disabled={loading}
                    >
                      <i className="fa-solid fa-xmark me-2"></i>
                      Cancelar
                    </button>

                    <button
                      type="submit"
                      form="form-jefe"
                      className="btn btn-dark"
                      disabled={loading}
                      title={isEdit ? "Actualizar" : "Guardar"}
                    >
                      <i
                        className={`fa-solid ${loading ? "fa-spinner fa-spin" : "fa-floppy-disk"
                          } me-2`}
                      ></i>
                      {isEdit ? "Actualizar" : "Guardar"}
                    </button>
                  </div>
                </div>
              </div>
            </form>

          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrarJefes;
